import React from 'react';
import { Link } from 'react-router-dom';
import {
  Archive,
  Camera,
  Crop,
  Download,
  Eye,
  Layers,
  Paintbrush,
  RefreshCw,
  RotateCcw,
  ScanSearch,
  Shield,
  SlidersHorizontal,
  Sparkles,
  Users,
} from 'lucide-react';
import { MarketingBeforeAfter } from '../components/MarketingBeforeAfter';
import { useAuth } from '../lib/AuthContext';

const NAV = [
  { id: 'analyze', label: 'Analyze' },
  { id: 'guide', label: 'Guide' },
  { id: 'restore', label: 'Restore' },
  { id: 'refine', label: 'Refine' },
  { id: 'preserve', label: 'Preserve' },
] as const;

function ProcessStep({
  n,
  icon,
  title,
  children,
  id,
}: {
  n: number;
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  id?: string;
}) {
  return (
    <section className="hiw-step" id={id}>
      <div className="hiw-step-head">
        <span className="hiw-step-num" aria-hidden="true">
          {n}
        </span>
        <span className="hiw-step-icon" aria-hidden="true">
          {icon}
        </span>
        <h2>{title}</h2>
      </div>
      <div className="hiw-step-body">{children}</div>
    </section>
  );
}

function FeatureCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="hiw-card">
      <strong>{title}</strong>
      <p>{children}</p>
    </div>
  );
}

function Callout({ children }: { children: React.ReactNode }) {
  return <blockquote className="hiw-callout">{children}</blockquote>;
}

export const HowItWorksPage: React.FC = () => {
  const { productConfig, balance } = useAuth();
  const isAlphaFree =
    productConfig?.restorationsCurrentlyFree ||
    balance?.restorationsCurrentlyFree ||
    productConfig?.creditMode === 'disabled' ||
    productConfig?.creditMode === 'metered' ||
    productConfig?.stage === 'alpha' ||
    !productConfig;
  const watermarkOn = productConfig?.watermarkExportsEnabled !== false;

  return (
    <main className="hiw">
      <section className="hiw-hero" aria-labelledby="hiw-hero-title">
        <p className="hiw-kicker">How Kinvera works</p>
        <h1 id="hiw-hero-title">A careful restoration starts before the first pixel changes.</h1>
        <p className="hiw-lede">
          Kinvera analyzes the photograph, maps the people in it, lets you correct what the AI sees,
          and restores damage while trying to preserve the original faces, composition, photographic
          medium, and character of the print.
        </p>

        <ol className="hiw-flow" aria-label="Restoration workflow">
          <li>Capture</li>
          <li>Analyze</li>
          <li>Review</li>
          <li>Restore</li>
          <li>Refine</li>
        </ol>

        <div className="kin-cta-row">
          <Link to="/login?next=%2Fapp" className="kin-btn kin-btn-primary">
            {isAlphaFree ? 'Try Kinvera during Alpha' : 'Restore a photo'}
          </Link>
          <a href="#preserve" className="kin-btn kin-btn-ghost">
            See what Kinvera preserves
          </a>
        </div>
      </section>

      <nav className="hiw-nav" aria-label="Page sections">
        {NAV.map((item) => (
          <a key={item.id} href={`#${item.id}`}>
            {item.label}
          </a>
        ))}
      </nav>

      <div className="hiw-content">
        <ProcessStep n={1} id="analyze" icon={<Camera />} title="Bring in the photograph">
          <p>
            Kinvera is built for physical family photographs — the ones that may have been snapped
            with a phone, scanned from an album, photographed through glass or protective plastic,
            captured crooked, faded, scratched, creased, stained, or washed out by glare.
          </p>
          <p className="text-sm text-slate-600">
            Kinvera can also recognize native digital photographs and apply conservative cleanup for
            issues such as glare, poor color balance, noise, or mild blur without pretending they are
            historical prints.
          </p>
          <div className="hiw-card-grid">
            <FeatureCard title="Upload from your device">
              JPEG, PNG, or WebP — single photos or several at once for batch work.
            </FeatureCard>
            <FeatureCard title="Google Photos">
              Google Photos import and save-back are being added during Alpha. Connect when available
              in your Studio account.
            </FeatureCard>
            <FeatureCard title="Batch queue">
              Add multiple family photos, review them one by one, then download restored results
              together.
            </FeatureCard>
          </div>
        </ProcessStep>

        <ProcessStep n={2} icon={<ScanSearch />} title="Kinvera inspects before it restores">
          <p>
            Before any restoration, Kinvera studies the photograph — not just “enhance this image.”
            The analysis first determines how the image was captured (native digital, phone photo of
            a print, scan, or copy), then the photographic medium, estimated era, fading or color
            shift, glare and reflections, perspective distortion, scratches, creases, tears, border
            clutter, and other capture problems.
          </p>
          <p>
            <strong>Medium awareness matters.</strong> A faded 1970s color print should not suddenly
            become a modern hyper-saturated digital photo. A black-and-white print should stay
            black-and-white unless you intentionally ask otherwise. A native digital photo should not
            be labeled as C-41 or Kodachrome merely because of color cast or noise.
          </p>
          <ul className="hiw-chip-row" aria-label="Example photographic media">
            <li>Black-and-white print</li>
            <li>Sepia</li>
            <li>Mid-century color</li>
            <li>Aged C-41 color</li>
            <li>Instant photograph</li>
            <li>Hand-tinted portrait</li>
            <li>Other historical media</li>
          </ul>
        </ProcessStep>

        <ProcessStep n={3} id="guide" icon={<Users />} title="Map the people, not just the damage">
          <div className="hiw-split">
            <div>
              <p>
                Kinvera uses <strong>face detection and subject mapping</strong> to locate visible
                faces and label them — Person #1, Person #2, Person #3 — so you can guide restoration
                for the right people.
              </p>
              <p>
                In group photos, Kinvera can use subject position, row/location, hair color and style,
                apparent age, gender guidance, nearby subjects, and clothing cues to help you tell
                who is who.
              </p>
              <p className="hiw-note">
                This is not biometric facial recognition. Kinvera does not identify a real-world
                person by matching them against an identity database.
              </p>
            </div>
            <div className="hiw-subject-map" aria-hidden="true">
              <div className="hiw-subject-stage">
                <span className="hiw-face hiw-face-1">1</span>
                <span className="hiw-face hiw-face-2">2</span>
                <span className="hiw-face hiw-face-3">3</span>
                <span className="hiw-face hiw-face-4">4</span>
              </div>
              <div className="hiw-subject-card">
                <strong>Person #4</strong>
                <span>Front row, right</span>
                <span>Young boy · Short dark hair</span>
              </div>
            </div>
          </div>
        </ProcessStep>

        <ProcessStep n={4} icon={<SlidersHorizontal />} title="You get the final say">
          <p>
            Automatic analysis can be wrong. Kinvera lets you correct apparent age, gender guidance,
            subject entries, and photographic medium before — or after — a restoration.
          </p>
          <Callout>
            The correction guides the restoration. It does not give Kinvera permission to replace
            the person&apos;s face with a generic age- or gender-based face.
          </Callout>
          <div className="hiw-split">
            <div>
              <p>
                Example: if faded hair on a young person is misread as gray, you can set a younger
                apparent age and re-render. If a child is misclassified by gender, you can correct
                that subject. These settings act as restoration safeguards — the geometry of the
                actual photograph remains authoritative.
              </p>
            </div>
            <div className="hiw-ui-mock" aria-hidden="true">
              <p className="hiw-ui-mock-label">Person #2</p>
              <div className="hiw-ui-mock-row">
                <span>Detected</span>
                <strong>Middle-Aged (50–65)</strong>
              </div>
              <div className="hiw-ui-mock-row hiw-ui-mock-row-active">
                <span>Correct to</span>
                <strong>Young Adult (18–30)</strong>
              </div>
              <div className="hiw-ui-mock-btn">
                <RefreshCw aria-hidden="true" /> Re-render with updated settings
              </div>
            </div>
          </div>
        </ProcessStep>

        <ProcessStep n={5} icon={<Crop />} title="Fix the capture before asking AI to fix the photograph">
          <p>
            A phone photo of a print is not the same as a flat scan. Kinvera gives you preparation
            tools so the AI works on a cleaner representation of the original.
          </p>
          <div className="hiw-card-grid">
            <FeatureCard title="Rotation">
              Turn incorrectly oriented captures upright before analysis and restoration.
            </FeatureCard>
            <FeatureCard title="Crop">
              Remove table surface, fingers holding the print, album borders, and other surrounding
              clutter.
            </FeatureCard>
            <FeatureCard title="Corner pin">
              Mark the four corners of a print shot at an angle and flatten it into a more
              scan-like rectangle before restoration.
            </FeatureCard>
          </div>
        </ProcessStep>

        <ProcessStep n={6} icon={<Paintbrush />} title="Tell Kinvera exactly where the damage is">
          <div className="hiw-split">
            <div>
              <p>
                The damage brush lets you paint over severe tears, deep scratches, folds, tape
                damage, chemical stains, or damaged emulsion. The painted mask tells the restoration
                process where deeper repair should be concentrated.
              </p>
              <p>
                Philosophy: target the damaged area rather than unnecessarily redrawing everything
                around it.
              </p>
            </div>
            <div className="hiw-mask-flow" aria-hidden="true">
              <div className="hiw-mask-panel">
                <span>Photo</span>
              </div>
              <span className="hiw-mask-plus">+</span>
              <div className="hiw-mask-panel hiw-mask-panel-red">
                <span>Painted damage</span>
                <i />
              </div>
              <span className="hiw-mask-arrow">→</span>
              <div className="hiw-mask-panel hiw-mask-panel-clean">
                <span>Targeted repair</span>
              </div>
            </div>
          </div>
        </ProcessStep>

        <ProcessStep n={7} id="restore" icon={<Shield />} title="Restore damage without redesigning the photograph">
          <p>
            Kinvera is AI-assisted <em>conservative</em> family-photo restoration — designed to clean
            this exact photograph, not invent a prettier reinterpretation of the scene.
          </p>
          <div className="hiw-card-grid hiw-card-grid-3">
            <FeatureCard title="Geometry matters">
              Designed to preserve the number of people, face placement, proportions, pose,
              expression, body position, hair silhouette, clothing, composition, and spacing between
              subjects.
            </FeatureCard>
            <FeatureCard title="Identity matters">
              The goal is the same person, cleaned and restored — not a more conventionally
              attractive AI interpretation.
            </FeatureCard>
            <FeatureCard title="Historical character matters">
              Attempts to retain film softness, tonal relationships, grain/character, period color,
              and the original photographic medium.
            </FeatureCard>
          </div>
          <p className="hiw-note">
            These are restoration safeguards and design goals — not mathematical guarantees. You
            should always inspect the result yourself.
          </p>
        </ProcessStep>

        <ProcessStep n={8} icon={<Eye />} title="When Kinvera can't know, it shouldn't pretend">
          <p>
            Old photographs sometimes contain information that truly no longer exists. Kinvera is
            instructed to prefer uncertainty over invention.
          </p>
          <ul className="hiw-bullet-list">
            <li>
              If an eye is completely destroyed by glare — do not invent a sharply detailed generic
              eye.
            </li>
            <li>
              If fingers are too blurred to resolve — do not manufacture perfect new fingers.
            </li>
            <li>
              If hairstyle detail is missing — preserve the surviving hair shape rather than invent
              elaborate new strands.
            </li>
          </ul>
          <Callout>
            A slightly unresolved authentic feature is better than a confidently invented one.
          </Callout>
        </ProcessStep>

        <ProcessStep n={9} icon={<Layers />} title="Inspect the restoration yourself">
          <p>
            After restoration, compare Original, Restored, and a split before/after slider. Zoom in —
            including facial zoom where available — and decide whether the result stayed faithful
            before you download it.
          </p>
          <div className="hiw-demo">
            <MarketingBeforeAfter
              showSupportingPoints={false}
              fallback={
                <div className="hiw-demo-fallback">
                  <Sparkles aria-hidden="true" />
                  <p>
                    Approved before/after demo assets appear here when present. Until then, open the
                    Studio to compare your own restorations with the built-in slider.
                  </p>
                </div>
              }
            />
          </div>
        </ProcessStep>

        <ProcessStep n={10} id="refine" icon={<RefreshCw />} title="Fine-tune the result">
          <p>
            Specialist Consultation offers one-click refinements for common issues, plus a custom
            instruction box for specific requests.
          </p>
          <div className="hiw-card-grid">
            <FeatureCard title="One-click refinements">
              Soften exaggerated facial creases, reduce remaining glare, preserve a delicate
              jawline, ease overly tight crops, or tone down excessive saturation — based on options
              available in Studio.
            </FeatureCard>
            <FeatureCard title="Custom instruction">
              Example: “The face looks too old. Keep the original softer cheek shape.” Or: “Reduce
              the remaining glare near the upper-right corner.”
            </FeatureCard>
          </div>
          <Callout>
            Refinements are guided from the original photograph rather than endlessly copying an AI
            copy of an AI copy — helping reduce generational drift.
          </Callout>
        </ProcessStep>

        <ProcessStep n={11} icon={<Users />} title="Correct subjects even after restoration">
          <p>
            If you notice after a restore that age or gender guidance was wrong, update the subject
            settings and choose <strong>Re-render with updated settings</strong>. Kinvera generates a
            new revision from the prepared source using your corrections. Earlier revisions remain
            available to compare.
          </p>
        </ProcessStep>

        <ProcessStep n={12} icon={<Archive />} title="Keep the original. Keep the revisions.">
          <p>
            Kinvera keeps your original upload, your prepared working source, restoration revisions,
            post-restore crops, and exports as distinct steps — so a later crop or refine does not
            silently destroy earlier work.
          </p>
          <ul className="hiw-bullet-list">
            <li>The original upload is not overwritten.</li>
            <li>Restoration revisions stay selectable.</li>
            <li>A crop after restoration does not destroy the earlier master.</li>
            <li>Refinement produces another revision rather than erasing history.</li>
          </ul>
        </ProcessStep>

        <ProcessStep n={13} icon={<Download />} title="Keep the result">
          <div className="hiw-card-grid">
            <FeatureCard title="Download &amp; share">
              Save the restored image locally or share from your device.
            </FeatureCard>
            <FeatureCard title="Library &amp; batch ZIP">
              Review past jobs, delete what you no longer need, and package multiple restored photos
              into one ZIP download.
            </FeatureCard>
            <FeatureCard title="Google Photos save-back">
              When connected during Alpha, you can save a restored export back to Google Photos.
            </FeatureCard>
          </div>
          {watermarkOn && (
            <p className="hiw-note">
              Free Alpha exports may include a Kinvera watermark while the service is being tested.
            </p>
          )}
        </ProcessStep>

        <ProcessStep n={14} icon={<Layers />} title="Work through a family batch">
          <p>
            Upload several photos into a queue. Kinvera analyzes each one. You review subjects,
            preparation, and restorations individually — then process the queue and download results
            together. Batch support means organized throughput, not fully unattended automation.
          </p>
        </ProcessStep>

        <section className="hiw-contrast" id="preserve" aria-labelledby="hiw-not-title">
          <h2 id="hiw-not-title">What Kinvera is not</h2>
          <div className="hiw-card-grid hiw-card-grid-contrast">
            <FeatureCard title="Not a beauty filter">
              It is not trying to make people younger, prettier, smoother, or more symmetrical.
            </FeatureCard>
            <FeatureCard title="Not face replacement">
              It is not designed to swap an old face for a generic AI face.
            </FeatureCard>
            <FeatureCard title="Not automatic colorization">
              Historical monochrome photographs remain monochrome unless you intentionally change
              that.
            </FeatureCard>
            <FeatureCard title="Not a modern restyle">
              The goal is not to turn a 1950s snapshot into a modern digital portrait.
            </FeatureCard>
            <FeatureCard title="Not uncontrolled invention">
              Missing visual information should stay conservative rather than become plausible
              fiction.
            </FeatureCard>
          </div>
        </section>

        <section className="hiw-hood" aria-labelledby="hiw-hood-title">
          <h2 id="hiw-hood-title">A lot happens before you see the result</h2>
          <p className="hiw-hood-lede">
            Craftsmanship and safeguards — not a single “enhance” button.
          </p>
          <ol className="hiw-pipeline">
            <li>Upload</li>
            <li>Image preparation</li>
            <li>Face detection</li>
            <li>Subject mapping</li>
            <li>Photographic medium analysis</li>
            <li>Damage analysis</li>
            <li>User corrections</li>
            <li>Conservative restoration</li>
            <li>Visual comparison</li>
            <li>Optional refinement</li>
            <li>Export</li>
          </ol>
        </section>

        <section className="hiw-finale" aria-labelledby="hiw-finale-title">
          <RotateCcw className="hiw-finale-icon" aria-hidden="true" />
          <h2 id="hiw-finale-title">Your photo has already survived the years.</h2>
          <p>Restore what&apos;s there — without replacing what made it yours.</p>
          <div className="kin-cta-row">
            <Link to="/login?next=%2Fapp" className="kin-btn kin-btn-primary">
              {isAlphaFree ? 'Try Kinvera during Alpha' : 'Restore a photo'}
            </Link>
            <Link to="/" className="kin-btn kin-btn-ghost">
              Return home
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
};
